import {useState} from "react";
import {formatShortNumberForSpeedDial} from "../../../tools";
import {GenericTable} from "@dvrt/graphql-widgets";
import DeleteSpeedDialButton from "./DeleteButton";
import UpdateButton from "./UpdateButton";
import CreateUpdateDialog from "./SaveDialog";
import {Page} from "../../pages";
import FormattedButton from "../../FormattedButton";
import {useConcertoGenericQuery} from "../../../hooks";
import Loading from "@/components/breadcrumbs/Loading";
import {useAuth} from "@/context/AuthProvider";
import {SpeedDialListFragmentDoc, SpeedDialListFragment} from "@/queries.generated";
import {FeatureRestriction, SpeedDialInput} from "@/api";
import {defineMessage, defineMessages} from "react-intl";

type Props = {
    extensionId: number;
};

export default ({extensionId}: Props) => {
    const [isCreateDialogOpen, setCreateDialogOpen] = useState(false);
    const auth = useAuth();

    const query = useConcertoGenericQuery<SpeedDialListFragment>("speedDials", SpeedDialListFragmentDoc, {
        extraVariables: {
            extensionId,
        },
        initialOrdering: ["shortNumber"],
    });

    if (query.loading) {
        return <Loading />;
    }

    const disabled =
        query.data?.items[0]?.extension.featuresRestrictions.includes(FeatureRestriction.SpeedDials) &&
        !auth.isSuperuser;

    const addButton = (
        <FormattedButton
            message={defineMessage({id: "new_speed_dial", defaultMessage: "New speed dial"})}
            onClick={() => setCreateDialogOpen(true)}
            disabled={disabled}
        />
    );

    return (
        <Page
            title={defineMessage({id: "speedDials.title", defaultMessage: "Speed dials"})}
            controls={addButton}
            inlineControls
        >
            {isCreateDialogOpen && (
                <CreateUpdateDialog
                    onSuccess={() => query.refetch()}
                    isOpen={isCreateDialogOpen}
                    close={() => setCreateDialogOpen(false)}
                    userId={extensionId}
                />
            )}
            <GenericTable<SpeedDialListFragment>
                query={query as any}
                labels={defineMessages({
                    shortNumber: {id: "shortNumber", defaultMessage: "Short number"},
                    targetNumber: {id: "targetNumber", defaultMessage: "Target number"},
                    description: {id: "description", defaultMessage: "Description"},
                    extensionId: {id: "unused", defaultMessage: "Unused"},
                    extension: {id: "unused", defaultMessage: "Unused"},
                    "extension.featuresRestrictions": {id: "unused", defaultMessage: "Unused"},
                })}
                hideColumns={["extensionId", "extension.featuresRestrictions", "extension.featuresRestrictions"]}
                extraColumns={[
                    {
                        path: "actions",
                        label: null,
                        renderer: (row) => {
                            const speedDialData: Omit<SpeedDialInput, "userId"> = {
                                id: row.id,
                                shortNumber: row.shortNumber,
                                targetNumber: row.targetNumber,
                                description: row.description,
                            };

                            return (
                                <span>
                                    <UpdateButton
                                        userId={extensionId}
                                        data={speedDialData}
                                        onSuccess={() => query.refetch()}
                                        disabled={disabled}
                                    />
                                    <DeleteSpeedDialButton
                                        id={row.id}
                                        onSuccess={() => query.refetch()}
                                        name={`${row.shortNumber} - ${row.targetNumber}`}
                                        disabled={disabled}
                                    />
                                </span>
                            );
                        },
                    },
                ]}
                dataRenderers={{
                    shortNumber(val) {
                        return formatShortNumberForSpeedDial(val);
                    },
                }}
                orderableFields="all"
            />
        </Page>
    );
};
